/**
 * Copyright 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { useApplicationEnvironmentsPaginatedV2LazyQuery } from "~/generated/graphql";
import { dateFormat } from "~/constants";
import { useCurrency } from "~/hooks";
import { formatMoney } from "~/tools";

export function useMappedEnvironments() {
  const { currency } = useCurrency();
  const currentMonthIndex = dayjs().month();
  const currentYear = dayjs().format(dateFormat.year);

  const [getData, { loading, error, refetch }] =
    useApplicationEnvironmentsPaginatedV2LazyQuery();

  type Props = {
    id: string;
    limit: number;
    page: number;
  };

  const handleGetData = async ({ id, limit, page }: Props) => {
    const result = await getData({
      variables: {
        id,
        page,
        limit,
        year: currentYear,
      },
    });
    const envs = result.data?.application?.environmentsV2?.environments ?? [];

    return envs.map((env) => ({
      id: env.id,
      name: env.name,
      savingsSuggestion:
        Number(env.cloudWasteAggregate?.cost ?? "0") > 0
          ? formatMoney(env.cloudWasteAggregate?.cost ?? "0", currency)
          : "-",
      cost: formatMoney(env.cost?.currentMonth ?? "0", currency),
      forecast: formatMoney(env.cost?.forecast ?? "0", currency),
      budget: formatMoney(
        env.budgetYearly?.budgetByMonth[currentMonthIndex] ?? "0",
        currency
      ),
      orgUnits: (env.orgUnitsV2?.orgUnits ?? []).map((orgUnit) => ({
        name: orgUnit.name,
        id: orgUnit.id,
        parentId: orgUnit.businessContext.id,
        envName: env.name,
        color: orgUnit.businessContext.color,
      })),
    }));
  };

  return [handleGetData, { loading, error, refetch }] as const;
}
